import { createClient } from '@supabase/supabase-js';
import { supabaseFetch } from './supabaseFetch';
import { isFullTier } from '../utils/userTier';

// Check if environment variables are available
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Add validation to prevent URL construction errors
let supabase;

try {
  if (!supabaseUrl) {
    console.error('Supabase URL is missing. Please check your environment variables.');
    // Create a mock client or handle the error appropriately
    supabase = {
      channel: () => ({
        on: () => ({
          subscribe: () => {},
        }),
        subscribe: async () => {},
        send: async () => {},
        unsubscribe: () => {},
      }),
    };
  } else {
    // Create the real Supabase client
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      global: { fetch: supabaseFetch },
    });
    console.log('Supabase client initialized successfully');
  }
} catch (error) {
  console.error('Error initializing Supabase client:', error);
  // Provide a fallback implementation
  supabase = {
    channel: () => ({
      on: () => ({
        subscribe: () => {},
      }),
      subscribe: async () => {},
      send: async () => {},
      unsubscribe: () => {},
    }),
  };
}

// Export the client
export { supabase };

// ============================================================
// Anonymous Auth — every user gets a Supabase Auth session
// ============================================================

let _authUserId = null;
let _accessToken = null;

const SIGN_IN_RETRY_MS = [1000, 3000];
// When this page's sign-in began, and the last good session it held — kept in
// memory only, so a sign-in that vanishes from storage can be put back
// (guest-session-loss: the stored copy went missing while the page still had it).
let _signedInAt = null;
let _lastSession = null;

// Why the last sign-in gave up (signup-cant-connect), or null: 'refused' (the
// server said no, e.g. a busy venue's sign-in limit), 'network', 'renew' (a
// stored sign-in that couldn't be renewed), 'slow' (past the open deadline),
// 'error'. The app shows the can't-connect screen while it is set.
let _signInFailure = null;
let _signInRun = null;
// The open never waits longer than this for sign-in before saying so
const SIGN_IN_DEADLINE_MS = 10000;

const isNetworkError = (error) => error?.name === 'AuthRetryableFetchError';

// Cache a signed-in session into this module's auth state — one place, so the
// existing-session and new-anonymous paths can't drift apart.
const adoptSession = (session) => {
  _authUserId = session?.user?.id ?? null;
  _accessToken = session?.access_token ?? null;
  _lastSession = session ?? null;
  _signedInAt = Date.now();
};

/**
 * Ensure the current browser has a Supabase Auth session.
 * - Returning users: getSession() finds the existing session (localStorage; a
 *   network call only when it has expired and must be renewed).
 * - OTP-authenticated users: getSession() finds their phone-linked session.
 * - First-time visitors: signInAnonymously() creates one (one-time network call).
 *
 * On success getAuthUserId() returns a UUID; otherwise getSignInFailure() says why.
 */
async function signIn() {
  _signInFailure = null;
  try {
    const { data, error: loadError } = await supabase.auth.getSession();
    const session = data?.session;
    if (session) {
      adoptSession(session);
      console.log('[AUTH] Existing session found:', _authUserId);
      return;
    }
    // A stored sign-in that couldn't be renewed over the network is still on
    // the device: never sign in as someone new over it (that would replace
    // this person's account here). Try again renews it.
    if (isNetworkError(loadError)) {
      console.error('[AUTH] Stored session could not be renewed:', loadError.message);
      _signInFailure = 'renew';
      return;
    }
    // Every read and write needs a session since the open-access lockdown,
    // so a failed sign-in (e.g. a busy venue's network) is retried twice.
    for (let attempt = 0; ; attempt += 1) {
      const { data: signedIn, error } = await supabase.auth.signInAnonymously();
      if (!error) {
        adoptSession(signedIn?.session);
        console.log('[AUTH] Anonymous session created:', _authUserId);
        return;
      }
      console.error('[AUTH] Anonymous sign-in failed:', error.message);
      if (attempt >= SIGN_IN_RETRY_MS.length) {
        _signInFailure = isNetworkError(error) ? 'network' : 'refused';
        return;
      }
      await new Promise((r) => setTimeout(r, SIGN_IN_RETRY_MS[attempt]));
    }
  } catch (err) {
    console.error('[AUTH] signIn error:', err);
    _signInFailure = 'error';
  }
}

// One sign-in at a time: a Try again during a slow first attempt joins it
// rather than racing it (two racing anonymous sign-ins = two people).
function ensureAuth() {
  if (!_signInRun) {
    _signInRun = signIn().finally(() => {
      _signInRun = null;
    });
  }
  return _signInRun;
}

// The open's sign-in, capped: past the deadline the app shows the
// can't-connect screen; the attempt carries on and Try again joins it.
function signInAtOpen() {
  let timer;
  const deadline = new Promise((resolve) => {
    timer = setTimeout(() => {
      if (!_authUserId && !_signInFailure) _signInFailure = 'slow';
      resolve();
    }, SIGN_IN_DEADLINE_MS);
  });
  return Promise.race([ensureAuth(), deadline]).finally(() => clearTimeout(timer));
}

// Intentional sign-out flag — distinguishes user-initiated sign-out from refresh failure
let _intentionalSignOut = false;

/**
 * Sign out intentionally (user action or account deletion).
 * Sets flag so the recovery listener knows not to intervene.
 */
export const signOutIntentionally = async () => {
  _intentionalSignOut = true;
  const result = await supabase.auth.signOut();
  // Flag is cleared in the onAuthStateChange listener after SIGNED_OUT fires,
  // not here — the event may fire asynchronously after this promise resolves.
  return result;
};

// Auth state listener — cache update + refresh failure recovery
supabase.auth?.onAuthStateChange((_event, session) => {
  _authUserId = session?.user?.id || null;
  _accessToken = session?.access_token || null;
  // A real sign-out clears the memory copy; a new session replaces it
  _lastSession = session || null;

  if (_event === 'SIGNED_OUT') {
    if (_intentionalSignOut) {
      _intentionalSignOut = false;
      return;
    }

    // Refresh token invalidated (multi-device rotation) — FULL users only.
    // Anon tokens never expire (session config: time-box=0, inactivity=0)
    // and Anon users don't multi-device, so no recovery needed for them.
    // A Simple profile rides that same anonymous session — routing it to the
    // OTP screen would strand it on a login it has no phone to complete.
    if (isFullTier()) {
      console.warn('[AUTH] Refresh failure detected (Profile). Triggering OTP re-login.');
      import('./analytics/eventLogger')
        .then(({ logEvent }) =>
          logEvent('auth_refresh_recovery', {
            user_type: 'profile',
            recovery_action: 'otp_redirect',
          })
        )
        .catch(() => {});
      sessionStorage.setItem('pending_otp_required', 'true');
      window.location.reload();
    }
  }
});

/** Resolves when auth is ready. Await before any Supabase DB call. */
export const authReady = supabaseUrl ? signInAtOpen() : Promise.resolve();

/**
 * Why sign-in gave up ('refused' | 'network' | 'renew' | 'slow' | 'error'), or
 * null while signed in or still trying. Read after authReady.
 */
export const getSignInFailure = () => (_authUserId ? null : _signInFailure);

/** Synchronous auth user ID. Returns UUID after authReady, null before. */
export const getAuthUserId = () => _authUserId;

/**
 * Synchronous access token of the current session (null before authReady).
 * For raw sends that can't wait — page-hide keepalive fetches — so they carry
 * the user's session instead of the public key (open-access lockdown).
 */
export const getAccessToken = () => _accessToken;

/**
 * Before a retry the person asked for: put back a sign-in that vanished from
 * storage, else sign in again (the refused-sign-in case). Never silent — the
 * caller only runs this from a visible "Try again". Gives up waiting after 8s.
 */
export const ensureSignedIn = () =>
  Promise.race([
    (async () => {
      if (await restoreLostSession()) return;
      await ensureAuth();
    })(),
    // Offline, a fresh sign-in waits for the network; Try again moves on
    new Promise((resolve) => setTimeout(resolve, 8000)),
  ]);

/**
 * Forget the held sign-in when this device's data is wiped (/clear, reset):
 * a sign-out that failed offline would otherwise leave it restorable.
 */
export const forgetHeldSession = () => {
  _lastSession = null;
};

/** When this page's sign-in began (ms), or null. For the failure recorder. */
export const getAuthSignedInAt = () => _signedInAt;

/**
 * Put back a sign-in that vanished from storage while this page still held it.
 * supabase-js re-reads the STORED session before every request and, finding
 * none, silently sends it signed-out (the public key) — the server then
 * refuses a guest's writes (guest-session-loss § Bug-check). Acts only in that
 * case: a stored session present means the failure is something else.
 *
 * @returns {Promise<boolean>} true = restored as the same user
 */
export const restoreLostSession = async () => {
  const held = _lastSession;
  if (!held?.access_token || !held?.refresh_token) return false;
  const { data } = await supabase.auth.getSession();
  if (data?.session) return false;
  const { data: restored, error } = await supabase.auth.setSession({
    access_token: held.access_token,
    refresh_token: held.refresh_token,
  });
  return !error && restored?.session?.user?.id === held.user?.id;
};

// Keep warmup channel alive to maintain WebSocket connection
let warmupChannel = null;
let warmupPromise = null;
let warmupResolve = null;

/**
 * Wait for the WebSocket warmup to complete.
 * Call this before joining a channel to ensure the connection is ready.
 * Returns immediately if warmup already completed.
 */
export const waitForWarmup = async () => {
  if (warmupPromise) {
    await warmupPromise;
  }
};

/**
 * Pre-warm the Supabase realtime WebSocket connection.
 * Call this early in app lifecycle so channel joins are fast.
 * The underlying WebSocket is established lazily on first channel use,
 * so warming it up prevents slow initial connections on custom domains.
 *
 * IMPORTANT: We keep the warmup channel subscribed because unsubscribing
 * when there are no other channels may close the WebSocket connection.
 */
export const warmupRealtimeConnection = async () => {
  // Create the promise that other code can await
  warmupPromise = new Promise((resolve) => {
    warmupResolve = resolve;
  });

  try {
    console.log('[SUPABASE] Warming up realtime connection...');
    const startTime = Date.now();

    // Create a persistent channel to establish AND MAINTAIN the WebSocket connection
    warmupChannel = supabase.channel('__warmup__');

    // Subscribe with a short timeout - we just need to establish the connection
    await new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.log('[SUPABASE] Warmup timeout (connection established in background)');
        warmupResolve(); // Signal warmup complete (even on timeout)
        resolve();
      }, 5000); // 5 second timeout for warmup

      warmupChannel.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          clearTimeout(timeout);
          const elapsed = Date.now() - startTime;
          console.log(`[SUPABASE] Realtime connection warmed up in ${elapsed}ms (keeping alive)`);
          // DON'T unsubscribe - keep channel alive to maintain WebSocket connection
          warmupResolve(); // Signal warmup complete
          resolve();
        } else if (status === 'TIMED_OUT' || status === 'CHANNEL_ERROR') {
          clearTimeout(timeout);
          console.warn(`[SUPABASE] Warmup failed with status: ${status}`);
          warmupResolve(); // Signal warmup complete (even on failure)
          resolve(); // Don't reject - warmup failure shouldn't block the app
        }
      });
    });
  } catch (error) {
    console.warn('[SUPABASE] Warmup error (non-fatal):', error);
    warmupResolve?.(); // Signal warmup complete (even on error)
    // Don't throw - warmup failure shouldn't block the app
  }
};

// Helper function to create or get a chat channel
export const getOrCreateChannel = async (channelId) => {
  try {
    return supabase.channel(channelId);
  } catch (error) {
    console.error('Error creating channel:', error);
    throw error;
  }
};

// Helper function to send a message to a channel
export const sendMessage = async (channelId, message, sender) => {
  try {
    const channel = supabase.channel(channelId);

    // Don't subscribe here - the channel should already be subscribed
    // This was causing the 'tried to subscribe multiple times' error

    return channel.send({
      type: 'broadcast',
      event: 'message',
      payload: {
        sender,
        message,
        timestamp: new Date().toISOString(),
      },
    });
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// Helper function to listen for messages on a channel
export const listenToChannel = (channelId, callback) => {
  try {
    const channel = supabase.channel(channelId);

    channel
      .on('broadcast', { event: 'message' }, (payload) => {
        callback(payload);
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  } catch (error) {
    console.error('Error listening to channel:', error);
    throw error;
  }
};
